/**
 * Vimium-Style Hint System Composable
 *
 * Provides keyboard-driven navigation for the flow builder using Vimium-inspired
 * letter hints. When activated (via 'f' key), displays letter badges on:
 * - Visible dropzones (for node insertion)
 * - Visible nodes (for opening/editing)
 *
 * Features:
 * - Viewport-aware: Only shows hints for visible elements
 * - Home row priority: Labels like A, S, D, F prioritized for ergonomics
 * - Multi-char support: For flows with many elements (AA, AB, etc.)
 * - One-shot mode: Automatically exits after selection
 * - Unified hint pool: Dropzones and nodes share the same label space
 */

import { ref, computed, Ref, watch } from 'vue'

/**
 * Base hint interface with common properties
 */
interface BaseHint {
  id: string
  label: string
  element: HTMLElement
  rect: DOMRect
}

/**
 * Hint for a dropzone (insertion point)
 */
export interface DropzoneHint extends BaseHint {
  type: 'dropzone'
  dropzoneData: {
    parentUuid: string
    index: number
  }
}

/**
 * Hint for an existing node (for editing/opening)
 */
export interface NodeHint extends BaseHint {
  type: 'node'
  nodeData: {
    uuid: string
  }
}

/**
 * Union type for all hint types
 */
export type FlowHint = DropzoneHint | NodeHint

export interface UseVimiumHintsOptions {
  /** Reference to the nodes container element */
  nodesContainer: Ref<HTMLElement | null>
  /** Reference to the flow builder viewport element */
  flowBuilder: Ref<HTMLElement | null>
  /** Callback when a dropzone hint is selected */
  onDropzoneSelect: (data: { parentUuid: string; index: number; element: HTMLElement }) => void
  /** Callback when a node hint is selected */
  onNodeSelect: (data: { uuid: string; element: HTMLElement }) => void
}

/**
 * Generate Vimium-style hint labels
 *
 * Prioritizes home row keys for ergonomics, then expands to full alphabet
 * and two-character combinations for larger flows.
 *
 * @param count Number of labels needed
 * @returns Array of hint labels
 */
export function generateHintLabels(count: number): string[] {
  const labels: string[] = []

  // Reserved keys that shouldn't be used for hints:
  // H, J, K, L = navigation, F = find mode toggle, S = search
  const reservedKeys = new Set(['H', 'J', 'K', 'L', 'F', 'S'])

  // Home row keys first (most ergonomic), excluding reserved
  const homeRow = 'ASDFG'.split('').filter(k => !reservedKeys.has(k)).join('')
  // Remaining letters, excluding reserved
  const otherLetters = 'QWERTYUIOPZXCVBNM'.split('').filter(k => !reservedKeys.has(k)).join('')
  // Combined for two-char generation
  const allLetters = homeRow + otherLetters

  let remaining = count

  // Phase 1: Single home row letters (most accessible)
  for (const letter of homeRow) {
    if (remaining <= 0) break
    labels.push(letter)
    remaining--
  }

  // Phase 2: Single other letters
  for (const letter of otherLetters) {
    if (remaining <= 0) break
    labels.push(letter)
    remaining--
  }

  // Phase 3: Two-character combinations starting with home row
  if (remaining > 0) {
    for (const first of homeRow) {
      for (const second of allLetters) {
        if (remaining <= 0) break
        labels.push(first + second)
        remaining--
      }
      if (remaining <= 0) break
    }
  }

  return labels
}

export function useVimiumHints(options: UseVimiumHintsOptions) {
  const { nodesContainer, flowBuilder, onDropzoneSelect, onNodeSelect } = options

  // State
  const isActive = ref(false)
  const isCapturing = ref(false)
  const hints = ref<FlowHint[]>([])
  const inputBuffer = ref('')

  /**
   * Check if an element is within the viewport
   */
  const isInViewport = (element: HTMLElement, viewportRect: DOMRect): boolean => {
    const rect = element.getBoundingClientRect()

    // Check if element intersects with viewport
    return !(
      rect.right < viewportRect.left ||
      rect.left > viewportRect.right ||
      rect.bottom < viewportRect.top ||
      rect.top > viewportRect.bottom
    )
  }

  /**
   * Collect all visible dropzones
   */
  const collectVisibleDropzones = (viewportRect: DOMRect): DropzoneHint[] => {
    if (!nodesContainer.value) return []

    const dropzoneElements: DropzoneHint[] = []

    // Query all dropzones with data attributes (excluding minimap)
    const dropzones = nodesContainer.value.querySelectorAll(
      '.flowbuilder-dropzone[data-dropzone-id]:not(.flow-map .flowbuilder-dropzone)'
    )

    dropzones.forEach((el) => {
      const element = el as HTMLElement

      // Skip if not visible in viewport
      if (!isInViewport(element, viewportRect)) return

      const dropzoneId = element.getAttribute('data-dropzone-id')
      const parentUuid = element.getAttribute('data-parent-uuid')
      const indexStr = element.getAttribute('data-index')

      if (!dropzoneId || !parentUuid || indexStr === null) return

      const index = parseInt(indexStr, 10)
      if (isNaN(index)) return

      dropzoneElements.push({
        type: 'dropzone',
        id: `dropzone-${dropzoneId}`,
        label: '', // Will be assigned after collection
        element,
        rect: element.getBoundingClientRect(),
        dropzoneData: {
          parentUuid,
          index
        }
      })
    })

    return dropzoneElements
  }

  /**
   * Collect all visible nodes
   */
  const collectVisibleNodes = (viewportRect: DOMRect): NodeHint[] => {
    if (!nodesContainer.value) return []

    const nodeElements: NodeHint[] = []

    // Query all nodes with data-node-id (excluding minimap)
    const nodes = nodesContainer.value.querySelectorAll(
      '[data-node-id]:not(.flow-map [data-node-id])'
    )

    nodes.forEach((el) => {
      const element = el as HTMLElement

      // Skip if not visible in viewport
      if (!isInViewport(element, viewportRect)) return

      const nodeUuid = element.getAttribute('data-node-id')
      if (!nodeUuid) return

      nodeElements.push({
        type: 'node',
        id: `node-${nodeUuid}`,
        label: '', // Will be assigned after collection
        element,
        rect: element.getBoundingClientRect(),
        nodeData: {
          uuid: nodeUuid
        }
      })
    })

    return nodeElements
  }

  /**
   * Collect all visible hints (both dropzones and nodes) and assign labels
   */
  const collectAllHints = (): FlowHint[] => {
    if (!nodesContainer.value || !flowBuilder.value) return []

    const viewportRect = flowBuilder.value.getBoundingClientRect()

    // Collect both types
    const dropzones = collectVisibleDropzones(viewportRect)
    const nodes = collectVisibleNodes(viewportRect)

    // Combine all hints
    const allHints: FlowHint[] = [...dropzones, ...nodes]

    // Sort by vertical position (top to bottom, left to right for same row)
    allHints.sort((a, b) => {
      const yDiff = a.rect.top - b.rect.top
      if (Math.abs(yDiff) > 20) return yDiff
      return a.rect.left - b.rect.left
    })

    // Assign labels
    const labels = generateHintLabels(allHints.length)
    allHints.forEach((hint, i) => {
      hint.label = labels[i]
    })

    return allHints
  }

  /**
   * Refresh hints - recollect visible elements
   */
  const refreshHints = () => {
    if (!isActive.value) return
    hints.value = collectAllHints()
  }

  /**
   * Activate hint mode (one-shot)
   */
  const activate = () => {
    isActive.value = true
    isCapturing.value = true
    inputBuffer.value = ''
    hints.value = collectAllHints()
  }

  /**
   * Deactivate hint mode
   */
  const deactivate = () => {
    isActive.value = false
    isCapturing.value = false
    inputBuffer.value = ''
    hints.value = []
  }

  /**
   * Clear the input buffer
   */
  const clearBuffer = () => {
    inputBuffer.value = ''
  }

  /**
   * Remove last character from input buffer
   */
  const clearLastChar = () => {
    inputBuffer.value = inputBuffer.value.slice(0, -1)
  }

  /**
   * Handle a key stroke
   *
   * Adds to input buffer and checks for matches.
   * If exact match found, triggers the appropriate selection callback
   * and exits hint mode (one-shot behavior).
   *
   * @param key Uppercase letter key
   */
  const handleKeyStroke = (key: string) => {
    inputBuffer.value += key

    // Find exact match
    const exactMatch = hints.value.find(
      (h) => h.label === inputBuffer.value
    )

    if (exactMatch) {
      // Found a match - trigger appropriate callback based on hint type
      if (exactMatch.type === 'dropzone') {
        onDropzoneSelect({
          parentUuid: exactMatch.dropzoneData.parentUuid,
          index: exactMatch.dropzoneData.index,
          element: exactMatch.element
        })
      } else {
        onNodeSelect({
          uuid: exactMatch.nodeData.uuid,
          element: exactMatch.element
        })
      }

      // One-shot mode: deactivate after selection
      deactivate()
      return
    }

    // Check if current buffer could still match any hints
    const possibleMatches = hints.value.filter(
      (h) => h.label.startsWith(inputBuffer.value)
    )

    if (possibleMatches.length === 0) {
      // No possible matches - clear buffer (user made a mistake)
      inputBuffer.value = ''
    }
  }

  /**
   * Get hints that match the current input buffer (for highlighting)
   */
  const matchingHints = computed(() => {
    if (!inputBuffer.value) return hints.value
    return hints.value.filter((h) => h.label.startsWith(inputBuffer.value))
  })

  /**
   * Check if a specific hint is highlighted (matches current input)
   */
  const isHintHighlighted = (hint: FlowHint): boolean => {
    if (!inputBuffer.value) return true
    return hint.label.startsWith(inputBuffer.value)
  }

  return {
    // State
    isActive,
    isCapturing,
    hints,
    inputBuffer,
    matchingHints,

    // Methods
    activate,
    deactivate,
    refreshHints,
    handleKeyStroke,
    clearBuffer,
    clearLastChar,
    isHintHighlighted
  }
}
